import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useBrainApi, useLiveData } from "../../api/brainApi";
import {
  BrainRun,
  Requirement,
  durationMsOf,
  filesChangedOf,
  formatBilling,
  formatNumber,
  formatSeconds,
  tokensOf,
} from "../../api/types";
import RunWaterfallChart from "../../components/RunWaterfallChart";
import {
  AlertBanner,
  PageTitle,
  Panel,
  StatTile,
  StatusChip,
  btnSecondary,
  inputClass,
  selectClass,
} from "../../components/controlPlaneUi";

const toLocalInput = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const defaultFromLocal = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return toLocalInput(d);
};

const shortId = (id: number) => (id > 999 ? `${id}` : `#${id}`);

const OverviewPage: React.FC = () => {
  const { instance } = useMsal();
  const api = useBrainApi();
  const [from, setFrom] = useState(defaultFromLocal);
  const [to, setTo] = useState("");
  const [tenant, setTenant] = useState("");
  const [status, setStatus] = useState("");

  const fetchRuns = useCallback(() => api.getRuns(200), [api]);
  const fetchReqs = useCallback(() => api.getRequirements(), [api]);

  const {
    data: runsData,
    error: runsError,
    loading: runsLoading,
  } = useLiveData<BrainRun[]>(fetchRuns, [fetchRuns], 10000);

  const { data: reqsData, error: reqsError } = useLiveData<Requirement[]>(
    fetchReqs,
    [fetchReqs],
    15000
  );

  const runs = useMemo(() => runsData ?? [], [runsData]);
  const requirements = useMemo(() => reqsData ?? [], [reqsData]);

  const filtered = useMemo(() => {
    const fromMs = from ? new Date(from).getTime() : null;
    const toMs = to ? new Date(to).getTime() : null;
    return runs.filter((r) => {
      const t = new Date(r.startedAt).getTime();
      if (fromMs !== null && t < fromMs) return false;
      if (toMs !== null && t > toMs) return false;
      if (tenant && r.tenant !== tenant) return false;
      if (status && r.status !== status) return false;
      return true;
    });
  }, [runs, from, to, tenant, status]);

  const tenants = useMemo(
    () => Array.from(new Set(runs.map((r) => r.tenant))).sort(),
    [runs]
  );

  const summary = useMemo(() => {
    const active = filtered.filter((r) => r.status === "Running").length;
    const totalTokens = filtered.reduce((a, r) => a + tokensOf(r), 0);
    const totalBilling = filtered.reduce((a, r) => a + r.billingWeight, 0);
    const totalFiles = filtered.reduce((a, r) => a + filesChangedOf(r), 0);
    const totalDuration = filtered.reduce((a, r) => a + durationMsOf(r), 0);
    const reqInRange = requirements.length;
    return {
      runCount: filtered.length,
      active,
      reqCount: reqInRange,
      totalTokens,
      totalBilling,
      totalFiles,
      totalDuration,
    };
  }, [filtered, requirements]);

  const error = runsError ?? reqsError;
  // An auth/token failure is not an API outage — don't mislabel it "is the API running?".
  const isAuthError =
    !!error &&
    /refresh token|login_required|consent_required|access token|unauthor|\b401\b|jwt/i.test(error);
  const reauthenticate = () => {
    sessionStorage.removeItem("brain.reauthAttempt");
    instance.loginRedirect({ scopes: ["openid", "profile", "email"], redirectStartPage: window.location.pathname });
  };
  const loading = runsLoading && !runsData;
  const hasFilters = Boolean(from || to || tenant || status);
  const resetFilters = () => {
    setFrom(defaultFromLocal());
    setTo("");
    setTenant("");
    setStatus("");
  };

  return (
    <div className="space-y-4 max-w-[1400px]">
      <PageTitle kind="Cluster" name="overview">
        <StatusChip status="healthy" pulse />
      </PageTitle>

      {error && (
        <AlertBanner variant="error">
          {isAuthError ? (
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>Sign-in needed — your session couldn't be refreshed. ({error})</span>
              <button className={btnSecondary} onClick={reauthenticate}>
                Sign in again
              </button>
            </span>
          ) : (
            <>{error} — is the API running?</>
          )}
        </AlertBanner>
      )}

      {loading ? (
        <Panel>
          <p className="text-sm text-slate-500 font-mono">Loading cluster state…</p>
        </Panel>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <StatTile label="Runs" value={formatNumber(summary.runCount)} />
            <StatTile label="Active" value={formatNumber(summary.active)} />
            <StatTile label="Requirements" value={formatNumber(summary.reqCount)} />
            <StatTile label="Tokens" value={formatNumber(summary.totalTokens)} />
            <StatTile label="Billing" value={formatBilling(summary.totalBilling)} />
            <StatTile label="Files" value={formatNumber(summary.totalFiles)} />
            <StatTile label="Duration" value={formatSeconds(summary.totalDuration)} />
          </div>

          <Panel
            title="Query filters"
            action={
              hasFilters ? (
                <button type="button" onClick={resetFilters} className={btnSecondary}>
                  Clear
                </button>
              ) : undefined
            }
          >
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              <label className="block">
                <span className="text-[10px] font-semibold uppercase text-slate-500">
                  From
                </span>
                <input
                  type="datetime-local"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className={`${inputClass} mt-1`}
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-semibold uppercase text-slate-500">
                  To
                </span>
                <input
                  type="datetime-local"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className={`${inputClass} mt-1`}
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-semibold uppercase text-slate-500">
                  Tenant
                </span>
                <select
                  value={tenant}
                  onChange={(e) => setTenant(e.target.value)}
                  className={`${selectClass} mt-1`}
                >
                  <option value="">all</option>
                  {tenants.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] font-semibold uppercase text-slate-500">
                  Status
                </span>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className={`${selectClass} mt-1`}
                >
                  <option value="">all</option>
                  <option value="Running">Running</option>
                  <option value="Succeeded">Succeeded</option>
                  <option value="Failed">Failed</option>
                </select>
              </label>
            </div>
          </Panel>

          <Panel title="Sync activity — waterfall">
            <RunWaterfallChart runs={filtered} />
          </Panel>

          <Panel
            title="Runs"
            action={
              <Link
                to="/console/runs"
                className="text-sm text-[#C9A260] hover:underline font-mono"
              >
                View all past runs →
              </Link>
            }
          >
            <p className="text-sm text-slate-600">
              {summary.runCount} run{summary.runCount === 1 ? "" : "s"} in range
              {summary.active > 0 ? ` · ${summary.active} active` : ""}. Recent:{" "}
              {filtered.slice(0, 3).map((r) => shortId(r.id)).join(", ") || "none"}.
            </p>
          </Panel>
        </>
      )}
    </div>
  );
};

export default OverviewPage;